import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {CheckoutsService} from '@store/checkouts/checkouts.service';
import {containsApiErrorCode, whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {catchError, exhaustMap, map, mergeMap, of, switchMap} from 'rxjs';
import {CheckoutsActions} from './checkouts.actions';
import {PaginationSelectors, RouterSelectors, SnackBarActions} from '@store/aviary/aviary.adapter';
import {openConfirmationDialog, openFormDialog, openNotificationDialog} from '@aviary/store/common/effect-dialog.helpers';
import {RenewItemDialog} from '@app/my-account/checked-out/actions/renew-item.dialog';
import {CheckoutsSelectors} from '@store/checkouts/checkouts.selectors';
import {RenewAllDialog} from '@app/my-account/checked-out/actions/renew-all.dialog';
import {SimpleNotificationDialog} from '@aviary/dialogs/simple-notification/simple-notification.dialog';
import {isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {HttpErrorResponse} from '@angular/common/http';
import {ViewClaimDialog} from '@app/my-account/checked-out/actions/view-claim.dialog';
import {FileClaimDialog} from '@app/my-account/checked-out/actions/file-claim.dialog';
import {FileClaimValue} from '@store/checkouts/checkouts.types';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {FileClaimSuccessDialog} from '@app/my-account/checked-out/actions/file-claim-success.dialog';
import {HoldsActions} from '@store/holds/holds.actions';
import {Action} from '@ngrx/store';
import {PatronActions} from '@store/patron/patron.actions';
import {FormatPipe} from '@aviary/pipes/format.pipe';

@Injectable({providedIn: 'root'})
export class CheckoutsEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: CheckoutsService = inject(CheckoutsService);
  private readonly formatPipe: FormatPipe = inject(FormatPipe);

  public loadItemsOutAndCheckoutClaimsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('CHECKED_OUT'),
      mergeMap(() => [
        CheckoutsActions.loadCheckoutClaims(),
        CheckoutsActions.loadCheckedOut()
      ])
    )
  );

  public loadCheckedOut = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.loadCheckedOut, CheckoutsActions.renewItemSuccess, CheckoutsActions.renewAllItemsSuccess),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.loadCheckedOut(queryParams).pipe(
        map(response => CheckoutsActions.loadCheckedOutSuccess(response)),
        catchError((error: unknown) => of(CheckoutsActions.loadCheckedOutFailure(error)))
      ))
    )
  );

  public loadCheckoutClaims = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.loadCheckoutClaims),
      switchMap(() => this.service.loadCheckoutClaims({pageSize: 200}).pipe( // Hardcoded page size because checkouts already has pagination on the same page.
        map(response => CheckoutsActions.loadCheckoutClaimsSuccess(response)),
        catchError((error: unknown) => of(CheckoutsActions.loadCheckoutClaimsFailure(error)))
      ))
    )
  );

  public loadNextCheckedOutDueDateOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('DASHBOARD'),
      map(() => CheckoutsActions.loadNextCheckedOutDueDate())
    )
  );

  public loadNextCheckedOutDueDate = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.loadNextCheckedOutDueDate),
      switchMap(() => this.service.loadNextCheckedOutDueDate().pipe(
        map(response => CheckoutsActions.loadNextCheckedOutDueDateSuccess(response)),
        catchError((error: unknown) => of(CheckoutsActions.loadNextCheckedOutDueDateFailure(error)))
      ))
    )
  );

  public loadBorrowingHistoryOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('BORROWING_HISTORY'),
      map(() => CheckoutsActions.loadBorrowingHistory())
    )
  );

  public loadBorrowingHistory = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.loadBorrowingHistory),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.loadBorrowingHistory(queryParams).pipe(
        map(response => CheckoutsActions.loadBorrowingHistorySuccess(response)),
        catchError((error: unknown) => of(CheckoutsActions.loadBorrowingHistoryFailure(error)))
      ))
    )
  );

  public loadHoldDataOnLoadBorrowingHistorySuccess = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.loadBorrowingHistorySuccess),
      withLatestFromSelectors(CheckoutsSelectors.BorrowingHistory.catalogRecordIds),
      map(([, ids]) => HoldsActions.loadHoldsForCatalogRecords(ids))
    )
  );

  public showRenewItem = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.showRenewItem),
      openConfirmationDialog(({checkout}) => ({
        componentClass: RenewItemDialog,
        data: {checkout},
        onConfirm: () => CheckoutsActions.renewItem(checkout)
      }))
    )
  );

  public renewItem = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.renewItem),
      exhaustMap(({checkout}) => this.service.renew(checkout.checkoutId, checkout.itemTitle).pipe(
        mergeMap(updatedCheckout => ([
          SnackBarActions.showCenteredMultilineSuccess([
            `${checkout.itemTitle} successfully renewed!`,
            `New Due Date: ${this.formatPipe.transform(updatedCheckout.dueDate, 'DATE_ONLY')}`
          ]),
          CheckoutsActions.renewItemSuccess()
        ])),
        catchError((error: unknown) => of(
          CheckoutsActions.renewItemFailure(error))
        )
      ))
    )
  );

  public showRenewAllItems = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.showRenewAllItems),
      withLatestFromSelectors(CheckoutsSelectors.CheckedOut.singleCheckout, PaginationSelectors.totalCount),
      openConfirmationDialog(([, firstCheckout, totalCount]) => ({
        componentClass: RenewAllDialog,
        data: {firstCheckout, additionalItemCount: totalCount - 1},
        onConfirm: () => CheckoutsActions.renewAllItems()
      }))
    )
  );

  public renewAll = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.renewAllItems),
      exhaustMap(() => this.service.renewAll().pipe(
        mergeMap((counts) => ([
          isPositiveNumber(counts.failureCount) ?
            CheckoutsActions.showSomeItemsNotRenewed(counts.successCount) :
            SnackBarActions.showSuccess('All items successfully renewed.'),
          CheckoutsActions.renewAllItemsSuccess()
        ])),
        catchError((error: unknown) => of(CheckoutsActions.renewAllItemsFailure(error)))
      ))
    )
  );

  public renewAllItemsFailure = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.renewAllItemsFailure),
      mergeMap(({error}): Action[] => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 400) return [];

        if (containsApiErrorCode('ENTITY_ID_NOT_FOUND', error)) {
          return [
            SnackBarActions.show('There are no checked-out items to renew.'),
            CheckoutsActions.loadCheckedOut(),
            PatronActions.loadAccountStatus()
          ];
        }

        return [CheckoutsActions.showSomeItemsNotRenewed(0)];
      })
    )
  );

  public showSomeItemsNotRenewed = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.showSomeItemsNotRenewed),
      openNotificationDialog(({renewedCount}) => ({
        componentClass: SimpleNotificationDialog,
        data: {
          title: isPositiveNumber(renewedCount) ? 'Some Items Renewed' : 'Items Cannot Be Renewed',
          message: `${isPositiveNumber(renewedCount) ? 'Some' : 'These'} items were not eligible for renewal due to existing holds from other patrons or if the maximum number of renewals has been reached.`
        }
      }))
    )
  );

  public showFileClaim = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.showFileClaim),
      openFormDialog(({checkout}) => ({
        componentClass: FileClaimDialog,
        data: {checkout},
        onConfirm: (value: FileClaimValue) => CheckoutsActions.fileClaim(checkout, value)
      }))
    )
  );

  public fileClaim = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.fileClaim),
      withLatestFromSelectors(PatronSelectors.Auth.id),
      exhaustMap(([{checkout, value}, patronId]) => this.service.fileClaim({
        ...value,
        checkoutId: checkout.checkoutId,
        patronId
      }).pipe(
        map(() => CheckoutsActions.fileClaimSuccess()),
        catchError((error: unknown) => of(
          CheckoutsActions.fileClaimFailure(error))
        )
      ))
    )
  );

  public showFileClaimSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.fileClaimSuccess),
      openNotificationDialog(() => ({
        componentClass: FileClaimSuccessDialog,
        data: undefined
      }))
    )
  );

  public reloadAfterFileClaimSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.fileClaimSuccess),
      mergeMap(() => ([
        CheckoutsActions.loadCheckoutClaims(),
        CheckoutsActions.loadCheckedOut()
      ]))
    )
  );

  public showViewClaim = createEffect(() =>
    this.actions.pipe(
      ofType(CheckoutsActions.showViewClaim),
      openNotificationDialog(({checkoutClaim}) => ({
        componentClass: ViewClaimDialog,
        data: {checkoutClaim}
      }))
    )
  );
}
